/**
 * Competition Manager Clip 4 — Manage Your Rider Roster
 * Flow: Dashboard → click Riders nav → Riders page (3 riders) →
 *       click Add Rider → fill form → Save → show many riders.
 * 750 frames @ 30fps = 25 seconds.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../../components/AutoCursor";
import { ScreenMock } from "../../components/ScreenMock";
import { FadeTransition } from "../../components/FadeTransition";
import { StepLabel } from "../../components/StepLabel";

const ACCENT = "#3b82f6";

export function ManageRidersGuide() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={65}><Title /></Sequence>
      <Sequence from={55} durationInFrames={635}>
        <FadeTransition durationInFrames={635} fadeIn={10} fadeOut={10}><Demo /></FadeTransition>
      </Sequence>
      <Sequence from={680} durationInFrames={70}><Done /></Sequence>
    </AbsoluteFill>
  );
}

/* ── Title / Done ── */

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 48, 65], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: ACCENT, textTransform: "uppercase", letterSpacing: 3, fontFamily: mono, marginBottom: 14 }}>Competition Manager Training</div>
      <div style={{ fontSize: 52, fontWeight: 800, color: "#fff" }}>Manage Your Rider Roster</div>
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
        <span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Riders are reusable across all competitions</div>
    </AbsoluteFill>
  );
}

/* ── Demo ── */

function Demo() {
  const frame = useCurrentFrame();

  // Page state
  const onRidersPage = frame >= 80;
  const showForm = frame >= 170 && frame < 420;
  const riderSaved = frame >= 410;
  const showManyRiders = frame >= 470;

  // Typing animations
  const nameText = "Harley Clifford";
  const nickText = "The King";
  const levelText = "Pro";
  const funFactText = "3x World Champion";

  const typed = (text: string, start: number) => {
    const chars = Math.max(0, Math.min(text.length, Math.floor((frame - start) / 2)));
    return text.slice(0, chars);
  };

  const typedName = typed(nameText, 200);
  const typedNick = typed(nickText, 250);
  const typedLevel = typed(levelText, 290);
  const typedFunFact = typed(funFactText, 320);

  const activeField =
    frame >= 320 && frame < 355 ? "funfact" :
    frame >= 290 && frame < 320 ? "level" :
    frame >= 250 && frame < 290 ? "nickname" :
    frame >= 200 && frame < 250 ? "name" :
    null;

  const photoUploaded = frame >= 370;
  const saveClicked = frame >= 395;

  return (
    <AbsoluteFill>
      <ScreenMock url={onRidersPage ? "raley.koda-software.com/riders" : "raley.koda-software.com/dashboard"}>
        <div style={shell}>
          {/* Topbar */}
          <div style={topbar}>
            <span style={brand}>RALEY</span>
            <span style={{ ...navLink, color: !onRidersPage ? "#e4e4e7" : "#71717a" }}>Competitions</span>
            <span id="riders-nav" style={{ ...navLink, color: onRidersPage ? "#e4e4e7" : "#71717a" }}>Riders</span>
            <span style={navLink}>Training</span>
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ padding: "2px 8px", fontSize: 9, fontWeight: 700, background: "linear-gradient(135deg, #f59e0b, #d97706)", color: "#fff", borderRadius: 3, fontFamily: mono }}>PRO</span>
              <span style={{ fontFamily: mono, fontSize: 11, color: "#71717a" }}>Takoda Mundy</span>
              <span style={{ fontSize: 11, color: "#52525b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.5 }}>Sign out</span>
            </div>
          </div>

          <div style={{ flex: 1, padding: 24, overflow: "hidden" }}>
            {!onRidersPage ? (
              /* ── Dashboard ── */
              <DashboardView />
            ) : (
              /* ── Riders Page ── */
              <RidersView
                showForm={showForm}
                typedName={typedName}
                typedNick={typedNick}
                typedLevel={typedLevel}
                typedFunFact={typedFunFact}
                activeField={activeField}
                photoUploaded={photoUploaded}
                saveClicked={saveClicked}
                riderSaved={riderSaved}
                showManyRiders={showManyRiders}
              />
            )}
          </div>
        </div>
        <AutoCursor points={CURSOR_POINTS} />
      </ScreenMock>
      <StepLabel text="Start from the Competitions dashboard" show={5} hide={70} color={ACCENT} />
      <StepLabel text="Click Riders in the top nav" show={50} hide={100} color={ACCENT} />
      <StepLabel text="Your rider roster — reusable across all competitions" show={100} hide={160} color={ACCENT} />
      <StepLabel text="Click Add Rider to create a new rider" show={140} hide={195} color={ACCENT} />
      <StepLabel text="Fill in the rider details" show={195} hide={355} color={ACCENT} />
      <StepLabel text="Drag a photo onto the avatar to upload" show={355} hide={390} color={ACCENT} />
      <StepLabel text="Click Save to add the rider" show={390} hide={460} color="#22c55e" />
      <StepLabel text="Build your roster — riders work across all competitions" show={470} hide={625} color={ACCENT} />
    </AbsoluteFill>
  );
}

const CURSOR_POINTS = [
  { x: 960, y: 400, frame: 0 },
  // Click Riders nav
  { target: "riders-nav", frame: 60 },
  { target: "riders-nav", frame: 80, click: true },
  // Click Add Rider
  { target: "add-rider-btn", frame: 140 },
  { target: "add-rider-btn", frame: 170, click: true },
  // Click name field
  { target: "name-field", frame: 190 },
  { target: "name-field", frame: 200, click: true },
  // Move to nickname
  { target: "nick-field", frame: 245 },
  { target: "nick-field", frame: 250, click: true },
  // Move to level
  { target: "level-field", frame: 285 },
  { target: "level-field", frame: 290, click: true },
  // Move to fun fact
  { target: "fun-fact-field", frame: 315 },
  { target: "fun-fact-field", frame: 320, click: true },
  // Drag photo to avatar
  { target: "avatar-circle", frame: 360 },
  { target: "avatar-circle", frame: 370, click: true },
  // Click save
  { target: "save-btn", frame: 390 },
  { target: "save-btn", frame: 395, click: true },
  // Rest
  { x: 960, y: 400, frame: 450 },
  { x: 960, y: 400, frame: 625 },
];

/* ── Dashboard View ── */

function DashboardView() {
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Competitions</div>
          <div style={{ fontSize: 13, color: "#71717a", marginTop: 4 }}>Competitions and events you manage or judge</div>
        </div>
        <div style={{ padding: "8px 16px", background: ACCENT, color: "#fff", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
          New Competition
        </div>
      </div>
      <div style={{ borderTop: "1px solid #2a2a32", paddingTop: 20 }}>
        <div style={{ fontSize: 10, fontWeight: 600, color: "#71717a", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12, fontFamily: mono }}>
          My Competitions
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <CompCard name="Moomba" />
          <CompCard name="Boardstock" />
        </div>
      </div>
    </>
  );
}

function CompCard({ name }: { name: string }) {
  return (
    <div style={{ background: "#111113", border: "1px solid #2a2a32", padding: 20 }}>
      <div style={{ fontSize: 16, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>{name}</div>
      <div style={{ display: "flex", gap: 8 }}>
        <span style={{ padding: "2px 8px", fontSize: 9, fontWeight: 600, textTransform: "uppercase", background: ACCENT, color: "#fff", fontFamily: mono, letterSpacing: 0.5 }}>Best Score (Max)</span>
        <span style={{ padding: "2px 8px", fontSize: 9, fontWeight: 600, textTransform: "uppercase", background: "#1a1a1e", color: "#71717a", fontFamily: mono, letterSpacing: 0.5, border: "1px solid #2a2a32" }}>3 Judges</span>
      </div>
    </div>
  );
}

/* ── Riders View ── */

const INITIAL_RIDERS = [
  { name: "Takoda", nick: "Taka", level: null, initials: "", hasPhoto: true, color: "#71717a" },
  { name: "Bentley Bland", nick: "Big B", level: "Pro", initials: "B", hasPhoto: false, color: "#22c55e" },
  { name: "Mikayo Mundy", nick: "Kayo", level: "Pro", initials: "M", hasPhoto: false, color: "#3b82f6" },
];

const MANY_RIDERS_EXTRA = [
  { name: "Harley Clifford", nick: "The King", level: "Pro", initials: "HC", color: "#ef4444" },
  { name: "Nic Rapa", nick: "Raptor", level: "Pro", initials: "NR", color: "#8b5cf6" },
  { name: "Dallas Friday", nick: "D-Fri", level: "Open", initials: "DF", color: "#eab308" },
  { name: "Amber Wing", nick: "Wings", level: "Juniors", initials: "AW", color: "#f97316" },
  { name: "Steel Lafferty", nick: "Steele", level: "Pro", initials: "SL", color: "#06b6d4" },
];

function RidersView({
  showForm,
  typedName,
  typedNick,
  typedLevel,
  typedFunFact,
  activeField,
  photoUploaded,
  saveClicked,
  riderSaved,
  showManyRiders,
}: {
  showForm: boolean;
  typedName: string;
  typedNick: string;
  typedLevel: string;
  typedFunFact: string;
  activeField: string | null;
  photoUploaded: boolean;
  saveClicked: boolean;
  riderSaved: boolean;
  showManyRiders: boolean;
}) {
  const allRiders = showManyRiders
    ? [...INITIAL_RIDERS, ...MANY_RIDERS_EXTRA]
    : riderSaved
      ? [...INITIAL_RIDERS, { name: "Harley Clifford", nick: "The King", level: "Pro", initials: "HC", hasPhoto: false, color: "#ef4444" }]
      : INITIAL_RIDERS;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Riders</div>
          <div style={{ fontSize: 13, color: "#71717a", marginTop: 4 }}>Your rider roster — reusable across all competitions</div>
        </div>
        <div id="add-rider-btn" style={{ padding: "8px 16px", background: ACCENT, color: "#fff", fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, cursor: "pointer" }}>
          Add Rider
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, position: "relative" }}>
        {allRiders.map((r) => (
          <RiderCard key={r.name} {...r} />
        ))}

        {/* Placeholder "New Rider" card behind the modal */}
        {showForm && !riderSaved && (
          <div style={{ background: "#111113", border: "1px dashed #2a2a32", padding: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: "50%", background: "#1a1a1e", border: "1px solid #2a2a32", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: "#52525b" }}>
                N
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#52525b" }}>New Rider</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "#71717a" }}>Edit</span>
              <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "#ef4444", padding: "1px 6px", border: "1px solid #ef4444" }}>Delete</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Modal overlay ── */}
      {showForm && !saveClicked && (
        <div style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,0.6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 100,
        }}>
          <div style={{
            width: 440, background: "#111113", border: "1px solid #2a2a32",
            borderRadius: 8, padding: 28,
            boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          }}>
            {/* Avatar + title */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div id="avatar-circle" style={{
                width: 44, height: 44, borderRadius: "50%",
                background: photoUploaded ? "#ef4444" : typedName ? ACCENT : "#1a1a1e",
                border: `2px solid ${photoUploaded ? "#ef4444" : typedName ? ACCENT : "#2a2a32"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 16, fontWeight: 700,
                color: "#fff",
                cursor: "pointer",
              }}>
                {photoUploaded
                  ? (typedName ? typedName.split(" ").map(w => w[0]).join("").slice(0, 2) : "HC")
                  : typedName ? typedName.split(" ").map(w => w[0]).join("").slice(0, 2) : "?"}
              </div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{typedName || "New Rider"}</div>
            </div>

            <FormField id="name-field" label="Name" value={typedName} placeholder="" active={activeField === "name"} />
            <FormField id="nick-field" label="Nickname" value={typedNick} placeholder={'e.g. "The King"'} active={activeField === "nickname"} />
            <FormField id="level-field" label="Level" value={typedLevel} placeholder="e.g. Pro, Juniors, Open" active={activeField === "level"} />
            <FormField id="fun-fact-field" label="Fun Fact" value={typedFunFact} placeholder="Something interesting about the rider" active={activeField === "funfact"} />

            <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
              <div id="save-btn" style={{
                padding: "8px 20px", fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                background: ACCENT, color: "#fff", cursor: "pointer", borderRadius: 4,
              }}>
                Save
              </div>
              <div style={{ padding: "8px 20px", fontSize: 12, fontWeight: 600, textTransform: "uppercase", color: "#71717a", cursor: "pointer" }}>
                Cancel
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function FormField({ id, label, value, placeholder, active }: { id: string; label: string; value: string; placeholder: string; active: boolean }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 9, fontWeight: 600, color: "#71717a", textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontFamily: mono }}>{label}</div>
      <div id={id} style={{
        width: "100%", boxSizing: "border-box",
        padding: "10px 12px", fontSize: 13, color: "#e4e4e7",
        background: "#0a0a0b", border: `1px solid ${active ? ACCENT : "#2a2a32"}`,
      }}>
        {value || <span style={{ color: "#52525b" }}>{placeholder}</span>}
        {active && <span style={{ color: ACCENT }}>|</span>}
      </div>
    </div>
  );
}

/* ── Rider Card ── */

function RiderCard({ name, nick, level, initials, hasPhoto, color }: {
  name: string;
  nick: string;
  level: string | null;
  initials?: string;
  hasPhoto?: boolean;
  color: string;
}) {
  return (
    <div style={{ background: "#111113", border: "1px solid #2a2a32", padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%",
          background: hasPhoto ? "#1a1a1e" : color,
          border: hasPhoto ? "1px dashed #2a2a32" : "none",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: hasPhoto ? 14 : 16, fontWeight: 700,
          color: hasPhoto ? "#52525b" : "#fff",
        }}>
          {hasPhoto ? "↑" : initials}
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>{name}</div>
          <div style={{ fontSize: 11, color: "#71717a" }}>
            &quot;{nick}&quot;
            {level && <span style={{ marginLeft: 6 }}>{level}</span>}
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "#71717a", cursor: "pointer" }}>Edit</span>
        <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", color: "#ef4444", padding: "1px 6px", border: "1px solid #ef4444", cursor: "pointer" }}>Delete</span>
      </div>
    </div>
  );
}

/* ── Styles ── */

const mono = "'JetBrains Mono', monospace";

const shell: React.CSSProperties = {
  width: "100%", height: "100%", background: "#0a0a0b",
  fontFamily: "'Inter', sans-serif", color: "#e4e4e7",
  display: "flex", flexDirection: "column",
};

const topbar: React.CSSProperties = {
  height: 40, background: "#111113", borderBottom: "1px solid #2a2a32",
  display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0,
};

const brand: React.CSSProperties = {
  fontFamily: mono, fontWeight: 800, fontSize: 14, letterSpacing: 2,
};

const navLink: React.CSSProperties = {
  fontSize: 12, fontWeight: 500, color: "#52525b", textTransform: "uppercase", letterSpacing: 0.5,
};
